import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { Modal } from './Modal';
import { NewInformationRequestModal } from './NewInformationRequestModal';
import { useToast } from './Toast';
import { formatDate } from '../lib/orgTimezone';
import { useCurrentUser } from '../lib/useCurrentUser';
import { useEntityPermission } from '../lib/usePermissions';

// §2.4 — cancelling a request is restricted to the archive tier on the server
// (super_admin + office administrator). Hide the control from roles the backend
// forbids so they don't hit a 403 only after clicking.
const CANCEL_ROLES = ['super_admin', 'administrator'];

/**
 * PropertySetupQueue — the FIRM side of the §2 property-request workflow, shown
 * on the client profile's Portal tab. Mirrors what the client sees in their
 * portal (same requests, same statuses) and adds the working actions: request
 * edits (note required), promote into a real property, and cancel (note
 * required). One source of truth — both sides read the same requests.
 */

export interface SetupRequest {
  id: string;
  client_id: string;
  property_name: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  account_number: string | null;
  status: 'pending' | 'edits_requested' | 'added' | 'cancelled';
  resulting_property_id: string | null;
  information_request_id: string | null;
  last_note: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS: Record<SetupRequest['status'], { label: string; variant: 'warning' | 'info' | 'success' | 'neutral' }> = {
  pending: { label: 'Pending', variant: 'warning' },
  edits_requested: { label: 'Edits requested', variant: 'info' },
  added: { label: 'Added', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
};

const identity = (r: SetupRequest) =>
  r.property_name || [r.address_line1, r.city, r.state].filter(Boolean).join(', ') || r.account_number || 'Untitled request';

export function PropertySetupQueue({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  const canCancel = CANCEL_ROLES.includes(currentUser?.role ?? '');
  // "Request information" opens a New information request (POST /api/information-requests)
  // → gate on information_request.can_add (matches the InformationRequests create gate).
  const canRequestInfo = useEntityPermission('information_request', 'can_add');
  const key = ['property-setup-requests', clientId];
  const [noteFor, setNoteFor] = useState<{ id: string; kind: 'request-edits' | 'cancel' } | null>(null);
  const [noteText, setNoteText] = useState('');
  // Info-request modal: null = closed; an object opens it (optionally pre-selecting a
  // promoted card's resulting property). Setup requests are client-initiated (from the
  // portal), so this queue only reviews them — creating an information request lives on
  // the Information requests section below (7/28 B2).
  const [infoReq, setInfoReq] = useState<{ propertyIds?: string[]; setupRequestId?: string } | null>(null);

  const q = useQuery<SetupRequest[]>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<SetupRequest[]>(`/api/property-setup-requests?client_id=${clientId}`, signal),
    enabled: !!clientId,
  });
  const requests = q.data ?? [];

  // Refresh the queue AND the BFF the client page reads (its Properties tab +
  // header "Properties" count refetch after a promote creates a real property).
  const invalidate = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ['client-view', clientId] }); };
  const onErr = (e: unknown) => {
    const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
    toast({ variant: 'error', message: body?.error || (e as Error).message });
  };

  const promote = useMutation({
    mutationFn: (id: string) => api.post(`/api/property-setup-requests/${id}/promote`),
    onSuccess: () => { toast({ variant: 'success', message: 'Property added.' }); invalidate(); },
    onError: onErr,
  });
  const noteAction = useMutation({
    mutationFn: ({ id, kind, note }: { id: string; kind: 'request-edits' | 'cancel'; note: string }) =>
      api.post(`/api/property-setup-requests/${id}/${kind}`, { note }),
    onSuccess: () => { toast({ variant: 'success', message: 'Updated.' }); setNoteFor(null); setNoteText(''); invalidate(); },
    onError: onErr,
  });

  return (
    <div style={{ marginBottom: 'var(--sp-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Property setup queue</h3>
          <p style={{ margin: '2px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            Properties this client requested from their portal — review them here; the client sees the same statuses. To ask this client for documents, use New request under Information requests below.
          </p>
        </div>
      </div>

      {q.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>
      ) : q.isError ? (
        <div style={{ padding: 'var(--sp-5)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', borderRadius: 'var(--r-lg)', color: 'var(--color-status-red)', textAlign: 'center', fontSize: 'var(--fs-sm)' }}>
          Couldn't load the setup queue. Refresh to try again.
        </div>
      ) : requests.length === 0 ? (
        <div style={{ padding: 'var(--sp-5)', background: 'var(--color-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-sm)' }}>
          No property setup requests yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {requests.map((r) => {
            const s = STATUS[r.status];
            const terminal = r.status === 'added' || r.status === 'cancelled';
            return (
              <div key={r.id} style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{identity(r)}</span>
                      <Badge variant={s.variant} dot>{s.label}</Badge>
                    </div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginTop: 2 }}>
                      {r.account_number ? `Account ${r.account_number} · ` : ''}Requested {formatDate(r.created_at)}
                    </div>
                    {r.last_note && (
                      <div style={{ marginTop: 'var(--sp-2)', padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
                        <span style={{ color: 'var(--color-ink-3)' }}>Note: </span>{r.last_note}
                      </div>
                    )}
                    {r.information_request_id && (
                      <div style={{ marginTop: 'var(--sp-2)' }}>
                        <a
                          onClick={() => navigate(`/information-requests/${r.information_request_id}`)}
                          style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}
                        >Information requested ↗</a>
                      </div>
                    )}
                  </div>
                  {r.status !== 'cancelled' && (
                    <div style={{ display: 'flex', gap: 'var(--sp-2)', flexShrink: 0, flexWrap: 'wrap' }}>
                      {canRequestInfo && (
                        <Button variant="secondary" size="sm" onClick={() => setInfoReq({ propertyIds: r.resulting_property_id ? [r.resulting_property_id] : undefined, setupRequestId: r.id })}>Request information</Button>
                      )}
                      {!terminal && (
                        <>
                          <Button variant="secondary" size="sm" onClick={() => { setNoteFor({ id: r.id, kind: 'request-edits' }); setNoteText(''); }}>Request edits</Button>
                          <Button variant="primary" size="sm" loading={promote.isPending && promote.variables === r.id} onClick={() => promote.mutate(r.id)}>Add property</Button>
                          {canCancel && (
                            <Button variant="ghost" size="sm" onClick={() => { setNoteFor({ id: r.id, kind: 'cancel' }); setNoteText(''); }} style={{ color: 'var(--color-status-red)' }}>Cancel</Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Note prompt (request-edits + cancel both require a note). */}
      <Modal
        open={!!noteFor}
        onClose={() => setNoteFor(null)}
        title={noteFor?.kind === 'cancel' ? 'Cancel this request' : 'Request edits from the client'}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setNoteFor(null)} disabled={noteAction.isPending}>Back</Button>
            <Button
              variant={noteFor?.kind === 'cancel' ? 'danger' : 'primary'}
              loading={noteAction.isPending}
              disabled={!noteText.trim()}
              onClick={() => noteFor && noteAction.mutate({ id: noteFor.id, kind: noteFor.kind, note: noteText.trim() })}
            >
              {noteFor?.kind === 'cancel' ? 'Cancel request' : 'Send'}
            </Button>
          </>
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            A note is required so the client knows {noteFor?.kind === 'cancel' ? 'why it was cancelled' : "what's needed"}.
          </span>
          <textarea
            value={noteText}
            autoFocus
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            style={{ fontFamily: 'inherit', fontSize: 'var(--fs-md)', padding: '8px 12px', width: '100%', resize: 'vertical', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', background: 'var(--color-card)', color: 'var(--color-ink)' }}
          />
        </label>
      </Modal>

      {/* D1/D2 — request documents from this client, contextual to a specific setup
          request/promoted property (a promoted card pre-selects its resulting
          property). General "new request for this client" lives on the Information
          requests section below (7/28 B2). */}
      <NewInformationRequestModal
        open={!!infoReq}
        defaultClientId={clientId}
        defaultPropertyIds={infoReq?.propertyIds}
        setupRequestId={infoReq?.setupRequestId}
        onClose={() => setInfoReq(null)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['information-requests-board'] }); invalidate(); }}
      />
    </div>
  );
}
